import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import AnimatedNumber from "@/components/motion/AnimatedNumber";
import Magnetic from "@/components/motion/Magnetic";
import { useMortgageCalculator, MortgageCalculator } from "./Calculator";

const APPLICATION_URL = "https://secure.dominionlending.ca/?app=25574&lang=en";

const HERO_IMG = "https://images.pexels.com/photos/31737860/pexels-photo-31737860.jpeg?auto=compress&cs=tinysrgb&w=1920";

export function monthlyPayment(principal, annualRate, years) {
  const r = annualRate / 100 / 12;
  const n = years * 12;
  if (principal <= 0) return 0;
  if (r === 0) return principal / n;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

export const fmt = (n) =>
  n.toLocaleString("en-CA", { maximumFractionDigits: 0 });

// Canadian-style semi-annual compounding, converted to the chosen payment frequency
export function periodicPayment(principal, annualRate, years, perYear) {
  const i = Math.pow(1 + annualRate / 100 / 2, 2 / perYear) - 1;
  const n = years * perYear;
  if (principal <= 0) return 0;
  if (i === 0) return principal / n;
  return (principal * i) / (1 - Math.pow(1 + i, -n));
}

export default function Hero({ onLead, show3D }) {
  const calc = useMortgageCalculator();

  const panels = [
    { label: "Purchase Price", value: calc.price, suffix: "" },
    { label: "Down Payment", value: calc.down, suffix: "" },
    { label: "Est. Payment", value: calc.payment, suffix: calc.freq.suffix },
  ];

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-24"
    >
      {!show3D && (
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Modern luxury Canadian home illuminated at night"
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/70 to-charcoal/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-transparent to-charcoal/50" />
        </div>
      )}
      {show3D && (
        <>
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/70 via-charcoal/30 to-transparent pointer-events-none" />
          <div className="absolute inset-0 bg-charcoal/40 lg:hidden pointer-events-none" />
        </>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="gold-label mb-7"
          >
            Private Mortgage Advisory · Canada
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.05] text-ivory"
          >
            YOUR NEXT MOVE,
            <br />
            <span className="gold-text-gradient italic">FINANCED WITH CONFIDENCE.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45 }}
            className="mt-7 text-base md:text-lg text-ivory/65 font-light leading-relaxed max-w-xl"
          >
            Personalized mortgage solutions designed around your goals. Expert guidance from application to closing.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Magnetic>
              <button
                data-testid="hero-get-preapproved-btn"
                onClick={() => onLead("pre-approval")}
                className="px-9 py-4 text-xs tracking-[0.2em] font-semibold text-charcoal bg-gold hover:bg-champagne transition-colors duration-300 rounded-sm"
              >
                GET PRE-APPROVED
              </button>
            </Magnetic>
            <Magnetic>
              <a
                href={APPLICATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-apply-btn"
                className="inline-flex items-center gap-2.5 px-9 py-4 text-xs tracking-[0.2em] font-semibold text-gold border border-gold/60 hover:bg-gold/10 hover:border-gold transition-colors duration-300 rounded-sm bg-white/5 backdrop-blur-sm"
              >
                <ShieldCheck size={14} strokeWidth={1.75} /> START YOUR APPLICATION
              </a>
            </Magnetic>
            <Magnetic>
              <button
                data-testid="hero-book-consultation-btn"
                onClick={() => onLead("consultation")}
                className="px-9 py-4 text-xs tracking-[0.2em] font-medium text-ivory border border-white/25 hover:border-gold/60 hover:text-gold transition-colors duration-300 rounded-sm bg-white/5 backdrop-blur-sm"
              >
                BOOK A CONSULTATION
              </button>
            </Magnetic>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.85 }}
            className="mt-10 text-sm text-ivory/60 font-light"
            data-testid="hero-agent-line"
          >
            With <span className="text-champagne">Akashdeep Ghai</span>, Mortgage Agent ·{" "}
            <a href="tel:+18257362193" data-testid="hero-phone-link" className="text-gold hover:text-champagne transition-colors duration-300">
              (825) 736-2193
            </a>
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-8 flex flex-wrap gap-3.5"
            data-testid="hero-live-panels"
          >
            {panels.map((p, i) => (
              <motion.div
                key={p.label}
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 6 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.9 }}
                className="glass-panel rounded-sm px-5 py-3"
                data-testid={`hero-panel-${i}`}
              >
                <p className="text-[9px] uppercase tracking-[0.22em] text-ivory/45 mb-1">{p.label}</p>
                <AnimatedNumber value={p.value} suffix={p.suffix} className="font-serif text-lg text-champagne" />
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-9 flex items-center gap-4 flex-wrap"
            data-testid="hero-brokerage-logos"
          >
            <span className="text-[9px] uppercase tracking-[0.32em] text-ivory/35">Brokerage Affiliation</span>
            <span className="h-px w-8 bg-gold/30" aria-hidden="true" />
            <img
              src="/logos/dlc.png"
              alt="Dominion Lending Centres"
              data-testid="hero-logo-dlc"
              className="h-9 rounded-[4px] shadow-[0_4px_16px_rgba(0,0,0,0.35)]"
            />
            <span className="h-10 w-10 rounded-[4px] bg-ivory flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.35)]">
              <img
                src="/logos/aimi.png"
                alt="A.I.M.I. Collective Mortgage Group"
                data-testid="hero-logo-aimi"
                className="h-9 w-9 object-contain"
              />
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="lg:col-span-5"
        >
          <MortgageCalculator calc={calc} onLead={onLead} />
        </motion.div>
      </div>
    </section>
  );
}
